import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { TgScreen } from "../components/TgScreen";
import { Card } from "../components/atoms";
import { useWallet } from "../wallet";
import { haptic } from "../lib/haptics";

// ── Local-storage keys we hand out of the cache wipe.
// Kept in sync with the constants in tx-history / incoming-notes /
// contacts-store. If any of those files renames their key, audit this
// list too.
const CACHE_KEYS = [
  "telecrypt.tx-history.v1",
  "telecrypt.incoming-notes.v1",
  "telecrypt.recent-contacts.v1",
];

const TELEMETRY_KEY = "zcashwallet.telemetry.v1";

interface Props {
  /** Open the recovery-phrase modal (same wiring as the Wallet banner). */
  onOpenBackup?: () => void;
}

// Narrow shape for the parts of WebApp we actually call here. The
// vite-env.d.ts ambient declaration doesn't list showConfirm /
// SettingsButton (older clients omit them), so we cast inline.
type TgConfirm = {
  showConfirm?: (
    message: string,
    cb?: (ok: boolean) => void,
  ) => void;
};

const readTelemetryPref = (): boolean => {
  if (typeof localStorage === "undefined") return true;
  try {
    const raw = localStorage.getItem(TELEMETRY_KEY);
    if (raw === null) return true;
    return raw !== "false";
  } catch {
    return true;
  }
};

const writeTelemetryPref = (enabled: boolean): void => {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(TELEMETRY_KEY, enabled ? "true" : "false");
  } catch {
    /* quota / privacy mode — silent */
  }
};

// Bridge for TG's native showConfirm with a graceful browser fallback.
// Returns a Promise so the callsites can `await` it without nesting.
const askConfirm = (message: string): Promise<boolean> =>
  new Promise((resolve) => {
    const wa = window.Telegram?.WebApp as (TelegramWebApp & TgConfirm) | undefined;
    if (wa?.showConfirm) {
      try {
        wa.showConfirm(message, (ok) => resolve(!!ok));
        return;
      } catch {
        /* fall through to browser confirm */
      }
    }
    // Outside Telegram (browser preview, dev mode) — native dialog.
    if (typeof window !== "undefined" && typeof window.confirm === "function") {
      resolve(window.confirm(message));
      return;
    }
    resolve(false);
  });

export function SettingsScreen({ onOpenBackup }: Props = {}) {
  const { reset } = useWallet();
  const [telemetry, setTelemetry] = useState<boolean>(() => readTelemetryPref());
  const [cacheCleared, setCacheCleared] = useState<boolean>(false);

  // Reset the transient "cache cleared" badge after a beat so the user
  // sees confirmation but the surface returns to its neutral state.
  useEffect(() => {
    if (!cacheCleared) return;
    const t = setTimeout(() => setCacheCleared(false), 2200);
    return () => clearTimeout(t);
  }, [cacheCleared]);

  const toggleTelemetry = useCallback(() => {
    haptic("light");
    setTelemetry((prev) => {
      const next = !prev;
      writeTelemetryPref(next);
      return next;
    });
  }, []);

  const clearLocalCache = useCallback(async () => {
    haptic("warning");
    const ok = await askConfirm(
      "Clear local cache? This removes your transaction history, incoming-note log, and saved contacts from this device. Your wallet and balance stay intact.",
    );
    if (!ok) return;
    if (typeof localStorage !== "undefined") {
      for (const key of CACHE_KEYS) {
        try {
          localStorage.removeItem(key);
        } catch {
          /* silent */
        }
      }
    }
    haptic("success");
    setCacheCleared(true);
  }, []);

  const resetWallet = useCallback(async () => {
    haptic("warning");
    const ok = await askConfirm(
      "This will erase the wallet from this device. You'll need your 12-word recovery phrase to restore. Continue?",
    );
    if (!ok) return;
    haptic("error");
    await reset();
    // No navigation needed — App.tsx's wallet gate re-renders the
    // setup flow as soon as the state flips back to "none".
  }, [reset]);

  return (
    <TgScreen title="Settings" bottomNav="settings">
      <div style={{ padding: "8px 16px 24px" }}>
        {/* ── About ─────────────────────────────────────────────────── */}
        <Section title="About">
          <Row label="App" value="Shieldz" />
          {/* TODO(version): expose package.json#version via a Vite define
              so we don't have to keep this string in sync by hand. */}
          <Row label="Version" value="0.1.0" />
          <a
            href="https://leokit.dev"
            target="_blank"
            rel="noreferrer"
            onClick={() => haptic("light")}
            style={{
              ...rowLast,
              textDecoration: "none",
              color: "inherit",
              justifyContent: "center",
              gap: 8,
            }}
            aria-label="Powered by LeoKit"
          >
            <span style={{ fontSize: 12, color: "var(--hint)" }}>Powered by</span>
            <img
              src="https://leokit.dev/logo.png"
              alt="LeoKit"
              height={16}
              style={{ height: 16, width: "auto", opacity: 0.85 }}
            />
          </a>
        </Section>

        {/* ── Display ───────────────────────────────────────────────── */}
        <Section title="Display">
          <Row label="Currency" value="USD" />
          <HintRow text="Coming soon: EUR, GBP, JPY, TRY" />
        </Section>

        {/* ── Network ───────────────────────────────────────────────── */}
        <Section title="Network">
          <Row
            label="Chain"
            value={
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "rgba(34,197,94,0.16)",
                  color: "#4ADE80",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#4ADE80",
                  }}
                />
                Mainnet
              </span>
            }
          />
          <HintRow text="Shielded ZEC mainnet — the only chain Shieldz talks to today." />
        </Section>

        {/* ── Privacy & Storage ─────────────────────────────────────── */}
        <Section title="Privacy & Storage">
          <ToggleRow
            label="Telemetry"
            sublabel="Anonymous performance + error counters. Helps us catch slow proofs and OOMs on older phones."
            value={telemetry}
            onChange={toggleTelemetry}
          />
          <ActionRow
            label={cacheCleared ? "Cache cleared" : "Clear local cache"}
            sublabel="Removes tx history, incoming-note log, and saved contacts from this device."
            onClick={clearLocalCache}
            danger={false}
          />
        </Section>

        {/* ── Recovery ──────────────────────────────────────────────── */}
        <Section title="Recovery">
          <ActionRow
            label="Show recovery phrase"
            sublabel="Your 12 words. Required to restore the wallet on a new device."
            onClick={() => {
              haptic("light");
              onOpenBackup?.();
            }}
            danger={false}
          />
          <HintRow text="The encrypted phrase is in Telegram CloudStorage. The decryption key lives only on this device. Lose the key, paste the phrase once and we re-encrypt." />
        </Section>

        {/* ── Danger zone ───────────────────────────────────────────── */}
        <Section title="Danger zone">
          <ActionRow
            label="Reset wallet"
            sublabel="Erase this wallet from this device. Restore from your recovery phrase to recover."
            onClick={resetWallet}
            danger
          />
        </Section>
      </div>
    </TgScreen>
  );
}

// ── Layout primitives ─────────────────────────────────────────────────
// We keep these local to the file — they're not reused elsewhere and
// hoisting them to atoms.tsx would just spread the surface area.

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 1,
          color: "var(--hint)",
          margin: "12px 4px 8px",
        }}
      >
        {title}
      </div>
      <Card style={{ padding: "2px 14px" }}>{children}</Card>
    </div>
  );
}

const rowBase: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "12px 0",
  borderBottom: "1px solid var(--hairline, rgba(255,255,255,0.045))",
  fontFamily: "var(--font-text)",
};

const rowLast: CSSProperties = {
  ...rowBase,
  borderBottom: "none",
};

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={rowBase}>
      <span style={{ fontSize: 14, color: "var(--text)" }}>{label}</span>
      <span
        style={{
          fontSize: 14,
          color: "var(--hint)",
          textAlign: "right",
          maxWidth: "60%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function LinkRow({
  label,
  value,
  href,
}: {
  label: string;
  value: string;
  href: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={() => haptic("light")}
      style={{ ...rowLast, textDecoration: "none", color: "inherit" }}
    >
      <span style={{ fontSize: 14, color: "var(--text)" }}>{label}</span>
      <span style={{ fontSize: 14, color: "var(--link, #4DA2FF)" }}>{value}</span>
    </a>
  );
}

function HintRow({ text }: { text: string }) {
  return (
    <div
      style={{
        padding: "10px 0 12px",
        fontSize: 12,
        lineHeight: 1.4,
        color: "var(--hint)",
      }}
    >
      {text}
    </div>
  );
}

function ToggleRow({
  label,
  sublabel,
  value,
  onChange,
}: {
  label: string;
  sublabel?: string;
  value: boolean;
  onChange: () => void;
}) {
  return (
    <div style={rowBase}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 14, color: "var(--text)" }}>{label}</div>
        {sublabel && (
          <div
            style={{
              fontSize: 12,
              color: "var(--hint)",
              marginTop: 4,
              lineHeight: 1.35,
            }}
          >
            {sublabel}
          </div>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={onChange}
        style={{
          width: 42,
          height: 26,
          borderRadius: 999,
          border: "none",
          cursor: "pointer",
          padding: 0,
          background: value
            ? "var(--tg-theme-button-color, #2c8acb)"
            : "var(--bg-3, #2c333a)",
          position: "relative",
          flexShrink: 0,
          transition: "background 160ms ease",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 3,
            left: value ? 19 : 3,
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: "#fff",
            transition: "left 160ms ease",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }}
        />
      </button>
    </div>
  );
}

function ActionRow({
  label,
  sublabel,
  onClick,
  danger,
}: {
  label: string;
  sublabel?: string;
  onClick: () => void;
  danger?: boolean;
}) {
  const fg = danger ? "#FCA5A5" : "var(--link, #4DA2FF)";
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...rowBase,
        background: "transparent",
        border: "none",
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        padding: "12px 0",
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 14, color: fg, fontWeight: danger ? 600 : 500 }}>
          {label}
        </div>
        {sublabel && (
          <div
            style={{
              fontSize: 12,
              color: "var(--hint)",
              marginTop: 4,
              lineHeight: 1.35,
            }}
          >
            {sublabel}
          </div>
        )}
      </div>
    </button>
  );
}
